import React, { useEffect } from 'react';
import { FileText, Download, CheckCircle2, ArrowRight, Briefcase, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const FreeChecklist = () => {
  
  // Auto-trigger download on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      const link = document.createElement('a');
      link.href = '/downloads/business-system-audit-checklist.pdf';
      link.download = 'Business-System-Audit-Checklist.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
      {/* Header */}
      <nav className="px-6 md:px-12 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Briefcase className="h-6 w-6 text-white" />
            <span className="font-semibold text-lg text-white">AIVORA Techlabs</span>
          </div>
          <span className="text-xs text-slate-400 hidden sm:block">Free Resource</span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12 md:py-20">
        <div className="max-w-2xl mx-auto text-center">
          
          {/* Success State */}
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Your Download Has Started!
            </h1>
            
            <p className="text-slate-500 mb-6">
              The <strong>Business System Audit Checklist</strong> should download automatically.<br/>
              If it doesn't, click the button below.
            </p>
            
            <a 
              href="/downloads/business-system-audit-checklist.pdf" 
              download="Business-System-Audit-Checklist.pdf"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors text-lg"
              data-testid="manual-download-btn"
            >
              <Download className="h-5 w-5" />
              Download Again
            </a>

            {/* What's Inside */}
            <div className="mt-10 pt-8 border-t border-slate-200 text-left">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 text-center">What's Inside:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Current Systems Audit',
                  'Budget Planning Guide',
                  'Vendor Evaluation Criteria',
                  'Implementation Readiness',
                  'Red Flags to Watch',
                  'Quick Score Guide'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA - Book Consultation */}
            <div className="mt-10 pt-8 border-t border-slate-200">
              <p className="text-slate-600 mb-4">
                Need expert help with your software decision?
              </p>
              <a 
                href="https://calendly.com/aivora-techlabs/quick-session"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                data-testid="book-consultation-btn"
              >
                <Calendar className="h-4 w-4" />
                Book a FREE 30-Min Consultation
                <ArrowRight className="h-4 w-4" />
              </a>
              <p className="text-xs text-slate-400 mt-3">
                No strings attached. We help SMEs make the right choice.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 border-t border-slate-700/50">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            Designed by AIVORA Techlabs
          </p>
          <p className="text-slate-600 text-xs">
            © {new Date().getFullYear()} All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FreeChecklist;
